import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumBookingStatus,
  enumBrokeredOrderStatus,
  enumLoadStatus,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { cn, ContentCard, LocalizedDate, TextField } from '@rematter/ui-web';
import { SmartDatePicker } from '@rematter/ui-web/components';
import { useAtom } from 'jotai';

import { BrokeredLoadFormFooterActions } from '../BrokeredLoadFormFooterActions';

import { SelectBrokeredOrder } from '~/modules/brokerage/_components/select-fields/SelectBrokeredOrder';
import {
  $brokeredLoadForm,
  $hasUnsavedChanges,
} from '~/modules/brokerage/_state/brokeredLoadDetail.state';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  BrokeredLoadFormState,
  BrokeredLoadRecord,
} from '~/modules/brokerage/types/brokeredLoad';
import { SelectBooking } from '~/shared/components/select-fields/SelectBooking';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectSalesOrder } from '~/shared/components/select-fields/SelectSalesOrder';
import { SelectSupplierAccount } from '~/shared/components/select-fields/SelectSupplierAccount';

type Props = {
  load: BrokeredLoadRecord;
  onDiscard?: () => void;
  onSaveUpdates?: () => void;
  isSaving?: boolean;
};

export const BrokeredLoadTabShipmentInfo: React.FC<Props> = ({
  load,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
}) => {
  const t = useTranslations();
  const [form, setForm] = useAtom($brokeredLoadForm);
  const [, setHasUnsavedChanges] = useAtom($hasUnsavedChanges);

  const isDisabled =
    load.status === enumLoadStatus.Voided ||
    load.status === enumLoadStatus.Reconciled;

  const handleFieldChange = React.useCallback(
    (field: keyof BrokeredLoadFormState) => (value: string) => {
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
      setHasUnsavedChanges(true);
    },
    [setForm, setHasUnsavedChanges],
  );

  const handleSelectChange = React.useCallback(
    (field: keyof BrokeredLoadFormState) => (value: string | undefined) => {
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
      setHasUnsavedChanges(true);
    },
    [setForm, setHasUnsavedChanges],
  );

  const handleSupplierChange = React.useCallback(
    (value: string | undefined) => {
      setForm((prev) => ({
        ...prev,
        supplierId: value,
        brokeredOrderId: undefined,
      }));
      setHasUnsavedChanges(true);
    },
    [setForm, setHasUnsavedChanges],
  );

  const handleDateChange = React.useCallback(
    (value: CalendarDate | undefined) => {
      setForm((prev) => ({
        ...prev,
        expectedShipDate: value,
      }));
      setHasUnsavedChanges(true);
    },
    [setForm, setHasUnsavedChanges],
  );

  const bookingValidStatuses = React.useMemo(
    () => [enumBookingStatus.Open],
    [],
  );
  const brokeredOrderValidStatuses = React.useMemo(
    () => [enumBrokeredOrderStatus.Open, enumBrokeredOrderStatus.InProgress],
    [],
  );
  const salesOrderValidStatuses = React.useMemo(
    () => [enumSalesOrderStatus.Open],
    [],
  );

  const shippedWeightsDisplay = React.useMemo(() => {
    const parts: string[] = [];
    if (load.heavyWeight?.value) {
      parts.push(`Heavy: ${load.heavyWeight.value} lbs`);
    }
    if (load.lightWeight?.value) {
      parts.push(`Light: ${load.lightWeight.value} lbs`);
    }
    return parts.length > 0 ? parts.join(' / ') : '-';
  }, [load.heavyWeight, load.lightWeight]);

  return (
    <div className="@BrokeredLoadTabShipmentInfo flex max-h-full flex-col gap-5 p-5">
      <ContentCard>
        <div className="bg-white px-6 py-4">
          <h3 className="mb-4 text-base font-bold text-neutral">
            {t.brokerage.loads.detail.sections.basicInfo}
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.supplier}
                </label>
                <p className="text-sm font-medium text-neutral">
                  {load.supplier?.name ?? '-'}
                </p>
              </div>
            ) : (
              <SelectSupplierAccount
                value={form.supplierId}
                label={t.brokerage.loads.detail.fields.supplier}
                onChange={handleSupplierChange}
              />
            )}
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.brokeredOrder}
                </label>
                <p className="text-sm font-medium text-neutral">
                  {load.brokeredOrder?.orderNumber
                    ? `BO-${load.brokeredOrder.orderNumber}`
                    : '-'}
                </p>
              </div>
            ) : (
              <SelectBrokeredOrder
                value={form.brokeredOrderId}
                label={t.brokerage.loads.detail.fields.brokeredOrder}
                onChange={handleSelectChange('brokeredOrderId')}
                supplierIds={form.supplierId ? [form.supplierId] : undefined}
                statuses={brokeredOrderValidStatuses}
              />
            )}
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.expectedShipDate}
                </label>
                <p className="text-sm font-medium text-neutral">
                  {load.expectedShipDate ? (
                    <LocalizedDate
                      date={load.expectedShipDate}
                      dateStyle="medium"
                    />
                  ) : (
                    '-'
                  )}
                </p>
              </div>
            ) : (
              <SmartDatePicker
                value={form.expectedShipDate}
                label={t.brokerage.loads.detail.fields.expectedShipDate}
                onChange={handleDateChange}
              />
            )}
            <div>
              <label className="text-xs font-medium text-neutral/70">
                {t.brokerage.loads.detail.fields.shippedWeights}
              </label>
              <p className="text-sm font-medium text-neutral">
                {shippedWeightsDisplay}
              </p>
            </div>
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.facility}
                </label>
                <p className="text-sm font-medium text-neutral">-</p>
              </div>
            ) : (
              <SelectFacility
                value={form.facilityId}
                label={t.brokerage.loads.detail.fields.facility}
                onChange={handleSelectChange('facilityId')}
              />
            )}
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.salesOrder}
                </label>
                <p className="text-sm font-medium text-neutral">
                  {load.salesOrderNumber ? `SO-${load.salesOrderNumber}` : '-'}
                </p>
              </div>
            ) : (
              <SelectSalesOrder
                value={form.salesOrderId}
                label={t.brokerage.loads.detail.fields.salesOrder}
                onChange={handleSelectChange('salesOrderId')}
                statuses={salesOrderValidStatuses}
              />
            )}
            {isDisabled ? (
              <div>
                <label className="text-xs font-medium text-neutral/70">
                  {t.brokerage.loads.detail.fields.booking}
                </label>
                <p className="text-sm font-medium text-neutral">
                  {load.bookingNumber ? `BK-${load.bookingNumber}` : '-'}
                </p>
              </div>
            ) : (
              <SelectBooking
                value={form.bookingId}
                label={t.brokerage.loads.detail.fields.booking}
                onChange={handleSelectChange('bookingId')}
                statuses={bookingValidStatuses}
              />
            )}
          </div>
        </div>
      </ContentCard>

      <ContentCard className={cn(!isDisabled && 'pb-0')}>
        <div className="bg-white px-6 py-2">
          <div className="flex flex-col gap-5">
            <div>
              <h3 className="mb-4 text-base font-bold text-neutral">
                {t.brokerage.loads.detail.sections.carrierInfo}
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <TextField
                  value={form.deliveryNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.deliveryNumber}
                  onChange={handleFieldChange('deliveryNumber')}
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.releaseNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.releaseNumber}
                  onChange={handleFieldChange('releaseNumber')}
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.containerNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.containerNumber}
                  onChange={handleFieldChange('containerNumber')}
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.sealNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.sealNumber}
                  onChange={handleFieldChange('sealNumber')}
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.truckNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.truckNumber}
                  onChange={handleFieldChange('truckNumber')}
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.trailerNumber ?? ''}
                  label={t.brokerage.loads.detail.fields.trailerNumber}
                  onChange={handleFieldChange('trailerNumber')}
                  isDisabled={isDisabled}
                />
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-base font-bold text-neutral">
                {t.brokerage.loads.detail.sections.notes}
              </h3>
              <div className="flex w-full flex-row justify-between gap-4">
                <TextField
                  value={form.recyclerInternalNotes ?? ''}
                  label={t.brokerage.loads.detail.notes.internalNotes}
                  onChange={handleFieldChange('recyclerInternalNotes')}
                  className="w-full"
                  textArea
                  isDisabled={isDisabled}
                />
                <TextField
                  value={form.recyclerExternalNotes ?? ''}
                  label={t.brokerage.loads.detail.notes.externalNotes}
                  onChange={handleFieldChange('recyclerExternalNotes')}
                  className="w-full"
                  textArea
                  isDisabled={isDisabled}
                />
              </div>
            </div>
          </div>
        </div>
        {!isDisabled && (
          <BrokeredLoadFormFooterActions
            onSaveUpdates={onSaveUpdates}
            onDiscard={onDiscard}
            isSaving={isSaving}
          />
        )}
      </ContentCard>
    </div>
  );
};
